import * as React from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card
} from '@mui/material';
import { useTranslation } from "react-i18next";
import AppInformation from './AppInformation';
import GeneralSettings from './GeneralSettings';
import LanguageSetting from './LanguageSetting';
import SMTPSettings from './SMTPSettings';
import SmsSettings from './SmsSettings';
import PaymentSettings from './PaymentSettings';
import CancellationReasons from './CancellationReasons';
import { FONT_FAMILY } from "../common/sharedFunctions";
import SettingsIcon from '@mui/icons-material/SettingsOutlined';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

export default function Settings() {
  const [value, setValue] = React.useState(0);
  const { t } = useTranslation();

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Card
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 24px -4px rgba(15, 23, 42, 0.06)',
          overflow: 'hidden'
        }}
      >
        {/* ── Settings Header ── */}
        <Box sx={{ p: { xs: 2.5, sm: 3 }, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              backgroundColor: 'rgba(229, 35, 42, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#E5232A'
            }}
          >
            <SettingsIcon fontSize="medium" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, color: '#0F172A', fontSize: '1.2rem' }}>
              {t('settings_title', { defaultValue: 'Platform Settings & Configuration' })}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: '#64748B', fontSize: '0.84rem', mt: 0.3 }}>
              Manage system credentials, payment gateways, commissions, localization, and messaging APIs.
            </Typography>
          </Box>
        </Box>

        {/* ── Sleek Tabs Bar ── */}
        <Box sx={{ borderBottom: '1px solid #F1F5F9', px: { xs: 1.5, sm: 3 }, backgroundColor: '#F8FAFC' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#E5232A',
                height: 3,
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTab-root': {
                fontFamily: FONT_FAMILY,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.86rem',
                minHeight: 48,
                color: '#64748B',
                px: 2.5,
                '&.Mui-selected': {
                  color: '#E5232A',
                  fontWeight: 700
                }
              }
            }}
          >
            <Tab label={t('app_info', { defaultValue: 'App Information' })} {...a11yProps(0)} />
            <Tab label={t('general_settings', { defaultValue: 'General Settings' })} {...a11yProps(1)} />
            <Tab label={t('language', { defaultValue: 'Localization' })} {...a11yProps(2)} />
            <Tab label={t('smtp', { defaultValue: 'SMTP Mail' })} {...a11yProps(3)} />
            <Tab label={t('smssettings', { defaultValue: 'SMS Gateway' })} {...a11yProps(4)} />
            <Tab label={t('payment_settings', { defaultValue: 'Payment Gateways' })} {...a11yProps(5)} />
            <Tab label={t('cancellation_reasons_title', { defaultValue: 'Cancellation Reasons' })} {...a11yProps(6)} />
          </Tabs>
        </Box>

        {/* ── Tab Panels ── */}
        <TabPanel value={value} index={0}>
          <AppInformation />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <GeneralSettings />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <LanguageSetting />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <SMTPSettings />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <SmsSettings />
        </TabPanel>
        <TabPanel value={value} index={5}>
          <PaymentSettings />
        </TabPanel>
        <TabPanel value={value} index={6}>
          <CancellationReasons />
        </TabPanel>
      </Card>
    </Box>
  );
}